import { atom } from 'jotai';

import { EnhancedContactDistributionReport } from '@/src/infrastructure/interfaces/IContactDistributionBulk';

const STORAGE_KEY = 'contact_distribution_processes';

// Load initial state from localStorage
const loadStoredProcesses = (): EnhancedContactDistributionReport[] => {
  if (typeof window === 'undefined') return [];
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return [];
  try {
    return JSON.parse(stored) as EnhancedContactDistributionReport[];
  } catch (error) {
    console.error('Error parsing stored processes:', error);
    localStorage.removeItem(STORAGE_KEY);
    return [];
  }
};

// Atom to store active processes
export const contactDistributionProcessesAtom = atom<EnhancedContactDistributionReport[]>(
  loadStoredProcesses()
);

/**
 * Atom to store active processes that are queued
 * While the process is queued, it is not shown in the list of active processes
 * because it is not yet started and has not a EnhancedContactDistributionReport
 * object.
 * When the process is started, it is removed from the list of queued processes
 * and added to the list of active processes.
 */
export const contactDistributionQueuedProcessesAtom = atom<string[]>([]);

// Action atom to add a process to the list of queued processes
export const addContactDistributionQueuedProcessAtom = atom(null, (get, set, processId: string) => {
  set(contactDistributionQueuedProcessesAtom, [
    ...get(contactDistributionQueuedProcessesAtom),
    processId,
  ]);
});

// Atom to store the polling interval ID
export const contactDistributionPollingIntervalAtom = atom<NodeJS.Timeout | null>(null);

// Derived atom to check if there are any active processes
export const hasActiveContactDistributionAtom = atom(
  (get) => get(contactDistributionProcessesAtom).length > 0
);

// Action atom to add a new process
export const addContactDistributionProcessAtom = atom(
  null,
  (get, set, newProcess: EnhancedContactDistributionReport) => {
    const processes = get(contactDistributionProcessesAtom);
    if (processes.some((p) => p.processId === newProcess.processId)) return;

    const updatedProcesses = [...processes, newProcess];
    set(contactDistributionProcessesAtom, updatedProcesses);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedProcesses));
  }
);

// Action atom to remove a process
export const removeContactDistributionProcessAtom = atom(null, (get, set, processId: string) => {
  const processes = get(contactDistributionProcessesAtom);
  const updatedProcesses = processes.filter((p) => p.processId !== processId);
  set(contactDistributionProcessesAtom, updatedProcesses);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedProcesses));
});

// Action atom to update a process
export const updateContactDistributionProcessAtom = atom(
  null,
  (get, set, updatedProcess: EnhancedContactDistributionReport) => {
    const processes = get(contactDistributionProcessesAtom);
    const updatedProcesses = processes.map((p) =>
      p.processId === updatedProcess.processId ? updatedProcess : p
    );
    set(contactDistributionProcessesAtom, updatedProcesses);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedProcesses));
  }
);
